<script setup lang="ts">
import { computed } from 'vue'

type CheckboxSize = 'sm' | 'md' | 'lg'

const props = withDefaults(defineProps<{
  modelValue?: boolean | (string | number)[]
  value?: string | number
  label?: string
  disabled?: boolean
  indeterminate?: boolean
  size?: CheckboxSize
}>(), {
  modelValue: false,
  value: undefined,
  label: '',
  disabled: false,
  indeterminate: false,
  size: 'md'
})

const emit = defineEmits<{
  'update:modelValue': [value: boolean | (string | number)[]]
  'change': [value: boolean | (string | number)[]]
}>()

const isChecked = computed(() => {
  if (Array.isArray(props.modelValue)) {
    return props.value !== undefined && props.modelValue.includes(props.value)
  }
  return props.modelValue
})

const wrapperClass = computed(() => [
  'base-checkbox',
  `base-checkbox--${props.size}`,
  {
    'base-checkbox--disabled': props.disabled,
    'base-checkbox--checked': isChecked.value,
    'base-checkbox--indeterminate': props.indeterminate
  }
])

function handleChange(): void {
  if (props.disabled) return

  if (Array.isArray(props.modelValue)) {
    if (props.value === undefined) return
    const newValue = isChecked.value
      ? props.modelValue.filter(v => v !== props.value)
      : [...props.modelValue, props.value]
    emit('update:modelValue', newValue)
    emit('change', newValue)
  } else {
    emit('update:modelValue', !props.modelValue)
    emit('change', !props.modelValue)
  }
}
</script>

<template>
  <label :class="wrapperClass">
    <span class="base-checkbox__input-wrapper">
      <input
        type="checkbox"
        class="base-checkbox__input"
        :checked="isChecked"
        :disabled="disabled"
        :indeterminate="indeterminate"
        @change="handleChange"
      />
      <span class="base-checkbox__box">
        <svg v-if="indeterminate" class="base-checkbox__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">
          <line x1="5" y1="12" x2="19" y2="12"></line>
        </svg>
        <svg v-else-if="isChecked" class="base-checkbox__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">
          <polyline points="20 6 9 17 4 12"></polyline>
        </svg>
      </span>
    </span>
    <span v-if="label || $slots.default" class="base-checkbox__label">
      <slot>{{ label }}</slot>
    </span>
  </label>
</template>

<style lang="scss" scoped>
.base-checkbox {
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-3);
  cursor: pointer;
  font-size: var(--text-sm);
  color: var(--text-secondary);

  &--disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &__input-wrapper {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__input {
    position: absolute;
    opacity: 0;
    width: 100%;
    height: 100%;
    cursor: pointer;

    &:disabled {
      cursor: not-allowed;
    }
  }

  &__box {
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--bg-tertiary);
    border: 1px solid var(--border-hover);
    /* 方框几何（≤4px 圆角）：落实"多选=方形勾框"的指示符规范——18px 盒上大圆角会与单选圆点同形混淆 */
    border-radius: 4px;
    transition: all var(--transition-fast);

    .base-checkbox--sm & {
      width: 16px;
      height: 16px;
    }

    .base-checkbox--md & {
      width: 18px;
      height: 18px;
    }

    .base-checkbox--lg & {
      width: 20px;
      height: 20px;
    }
  }

  &__icon {
    color: var(--white);
    stroke-linecap: round;
    stroke-linejoin: round;

    .base-checkbox--sm & {
      width: 10px;
      height: 10px;
    }

    .base-checkbox--md & {
      width: 12px;
      height: 12px;
    }

    .base-checkbox--lg & {
      width: 14px;
      height: 14px;
    }
  }

  &:hover:not(.base-checkbox--disabled) &__box {
    border-color: var(--primary);
  }

  &--checked &__box,
  &--indeterminate &__box {
    background: var(--primary);
    border-color: var(--primary);
  }

  &__label {
    user-select: none;
  }
}
</style>
